import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, DialogActions, Typography } from '@mui/material';
import React, { useState } from 'react';
import DefaultModal from '../../components/Modals/DefaultModal';
import SuccessModal from '../../components/Modals/SuccessModal';
import { BulkUploadSummary } from '../../models/bulkUploadSummary';
import { bulkPaymentsService } from '../../services/bulkPaymentsService';
import { createDownloadFile } from '../../utils/helpers';

const SummaryNotification = ({
  error,
  nrOfIssues
}: {
  error?: boolean;
  nrOfIssues: number;
}) => {
  return (
    <Box
      sx={{
        margin: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#EEF0F7',
        width: 300,
        height: 55,
        my: 4,
        padding: '10px 0 10px 20px',
        fontWeight: 'bold',
        borderRadius: '4px'
      }}
    >
      {error ? (
        <ErrorOutlineIcon sx={{ color: '#FF2D55' }} />
      ) : (
        <CheckCircleOutlineIcon sx={{ color: '#6AC021' }} />
      )}
      {error ? 'ERRORS' : 'SUCCESSFUL'}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: error ? '#FF2D55' : '#6AC021',
          width: 60,
          height: 55,
          color: 'white',
          borderTopRightRadius: '4px',
          borderBottomRightRadius: '4px'
        }}
      >
        {nrOfIssues ?? '--'}
      </Box>
    </Box>
  );
};

interface ImportSummaryProps {
  openModal: boolean;
  closeModal: () => void;
  onSuccess: () => void;
  paymentSummary: BulkUploadSummary | undefined;
}

const ImportSummaryModal = (props: ImportSummaryProps): JSX.Element => {
  const { openModal, closeModal, onSuccess, paymentSummary } = props;
  const [openSuccessModal, setOpenSuccessModal] = useState(false);

  const onSubmit = () => {
    if (paymentSummary) {
      bulkPaymentsService.submitPayments(paymentSummary.uuid).then(() => {
        closeModal();
        setOpenSuccessModal(true);
      });
    }
  };

  const handleSuccess = () => {
    setOpenSuccessModal(false);
    onSuccess();
  };

  const exportErrors = () => {
    if (paymentSummary) {
      bulkPaymentsService.exportErrors(paymentSummary.uuid).then((response) => {
        createDownloadFile(response.data, 'paymentErrors');
        closeModal();
      });
    }
  };

  return (
    <>
      <DefaultModal open={openModal} handleClose={closeModal}>
        <Box sx={{ px: 8, textAlign: 'center' }}>
          <Typography variant="h4" color="grey.900">
            Import Summary
          </Typography>
          {!!paymentSummary?.failedRecords && (
            <>
              <SummaryNotification
                error
                nrOfIssues={paymentSummary?.failedRecords}
              />
              {paymentSummary?.failedRecords > 1 ? (
                <Typography fontWeight="bold">
                  {paymentSummary?.failedRecords} errors were found in your
                  upload.
                </Typography>
              ) : (
                <Typography fontWeight="bold">
                  {paymentSummary?.failedRecords} error was found in your
                  upload.
                </Typography>
              )}

              <Typography>
                Those errors are outlined in the .xls available for download
                below. You must export the file below, fix any errors, and
                re-upload those rows in order for those payments to be
                processed.
              </Typography>
            </>
          )}
          {!!paymentSummary?.successRecords && (
            <>
              <SummaryNotification
                nrOfIssues={paymentSummary?.successRecords}
              />
              {paymentSummary?.successRecords > 1 ? (
                <Typography fontWeight="bold">
                  {paymentSummary?.successRecords} payments had no errors.
                </Typography>
              ) : (
                <Typography fontWeight="bold">
                  {paymentSummary?.successRecords} payment had no errors.
                </Typography>
              )}

              <Typography>
                To submit these payments use the &quot;SUBMIT&quot; button
                below. These payments will be processed by AllWork and issued to
                talent.
              </Typography>
            </>
          )}
        </Box>
        <DialogActions sx={{ justifyContent: 'center', py: 4 }}>
          <Button
            variant="outlined"
            onClick={exportErrors}
            disabled={!paymentSummary?.failedRecords}
          >
            Export Errors
          </Button>
          <Button onClick={onSubmit} disabled={!paymentSummary?.successRecords}>
            Submit {paymentSummary?.successRecords}
            {paymentSummary && paymentSummary?.successRecords > 1
              ? ' payments'
              : ' payment'}
          </Button>
        </DialogActions>
      </DefaultModal>
      <SuccessModal open={openSuccessModal} handleClose={handleSuccess}>
        <Typography align="center" fontWeight="bold">
          You have submitted {paymentSummary?.successRecords} payments.
        </Typography>
        <Typography align="center">
          These will be processed by AllWork team and talent will be notified.
        </Typography>
      </SuccessModal>
    </>
  );
};

export default ImportSummaryModal;
